import { useCallback, useEffect, useMemo, useState } from "react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { MESI, GIORNI, dateIt } from "@/lib/constants";
import { AgendaDialog } from "@/components/AgendaDialog";
import { AttivitaDialog } from "@/components/AttivitaDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, NotebookPen, CheckCircle2, Circle, AlertTriangle, Users } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function useAgenda(portfolioId) {
  const { api, activeSettore } = useCrm();
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.listAgenda(portfolioId).then(setItems).catch(() => setItems([])), [api, portfolioId]);
  useEffect(() => { load(); }, [load, activeSettore]);
  const save = async (editing, data) => {
    if (editing?.id) await api.updateAgenda(editing.id, data);
    else await api.createAgenda(data);
    toast.success(data.prossimo_passo && data.prossimo_data && !editing?.followup_id ? "Salvato · promemoria creato" : "Voce salvata");
    await load();
  };
  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.titolo}"?`)) return false;
    await api.deleteAgenda(a.id);
    toast.success("Voce eliminata");
    await load();
    return true;
  };
  const toggle = async (a) => { await api.updateAgenda(a.id, { stato: a.stato === "fatto" ? "da_fare" : "fatto" }); await load(); };
  return { items, save, remove, toggle };
}

export function AgendaRow({ a, onOpen, onToggle, shared }) {
  return (
    <div data-testid={`agenda-row-${a.id}`} onClick={onOpen}
      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${shared ? "border-sky-200 bg-sky-50/50 dark:border-sky-500/20 dark:bg-sky-500/5" : "border-slate-200 dark:border-slate-800 hover:border-primary/40"}`}>
      {shared ? <Users className="h-4 w-4 text-sky-500" /> : (
        <button data-testid={`agenda-toggle-${a.id}`} onClick={(e) => { e.stopPropagation(); onToggle(a); }}>
          {a.stato === "fatto" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300 hover:text-primary" />}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium truncate ${a.stato === "fatto" ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"}`}>{a.titolo}</div>
        <div className="text-xs text-slate-400 truncate">{shared ? `Team · ${a.ssd_nome || ""}` : [a.tipo, a.portfolio_nome].filter(Boolean).join(" · ")}{a.esito ? ` — ${a.esito}` : ""}</div>
      </div>
      <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{dateIt(a.data)}{a.ora ? ` · ${a.ora}` : ""}</span>
    </div>
  );
}

function Section({ title, icon: Icon, tone, list, tid, render }) {
  return (
    <Card className="p-5" data-testid={tid}>
      <div className={`flex items-center gap-2 mb-3 font-semibold ${tone}`}><Icon className="h-4 w-4" /> {title} <span className="text-xs text-slate-400 font-normal">({list.length})</span></div>
      <div className="space-y-2">{list.length === 0 ? <p className="text-sm text-slate-400">Niente qui.</p> : list.map(render)}</div>
    </Card>
  );
}

export default function Agenda() {
  const { portfolio, attivita, api, refresh } = useCrm();
  const [teamEdit, setTeamEdit] = useState(null);
  const saveTeam = async (data, opts = {}) => {
    const saved = await api.updateAttivita(teamEdit.id, data);
    toast.success("Evento aggiornato");
    if (opts.invite) {
      try { const r = await api.inviaInvito(saved.id); toast.success(`Invito inviato a ${r.inviati} partecipanti`); }
      catch (e) { toast.error(e?.response?.data?.detail || "Invio invito non riuscito"); }
    }
    setTeamEdit(null);
    await refresh();
  };
  const deleteTeam = async (a) => {
    if (!window.confirm(`Eliminare l'evento "${a.titolo}"?`)) return;
    await api.deleteAttivita(a.id);
    toast.success("Evento eliminato");
    setTeamEdit(null);
    await refresh();
  };
  const { isTitolare } = useAuth();
  const { items, save, remove, toggle } = useAgenda();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preset, setPreset] = useState("");
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const today = iso(now);
  const in7 = iso(new Date(now.getTime() + 7 * 864e5));

  const openItem = (a) => { setEditing(a); setOpen(true); };
  const lists = useMemo(() => {
    const todo = items.filter((a) => a.stato !== "fatto");
    const shared = attivita.filter((a) => a.stato !== "fatto" && a.data >= today && a.data <= in7);
    return {
      late: todo.filter((a) => a.data < today),
      today: todo.filter((a) => a.data === today),
      next: [...todo.filter((a) => a.data > today && a.data <= in7), ...shared]
        .sort((a, b) => (a.data + (a.ora || "")).localeCompare(b.data + (b.ora || ""))),
    };
  }, [items, attivita, today, in7]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const arr = Array(offset).fill(null);
    for (let d = 1; d <= days; d++) {
      const ds = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      arr.push({ d, ds, mine: items.filter((a) => a.data === ds), team: attivita.filter((a) => a.data === ds) });
    }
    return arr;
  }, [cursor, items, attivita]);
  const move = (delta) => setCursor(({ y, m }) => { m += delta; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } return { y, m }; });

  if (!isTitolare) return <Card className="p-10 text-center text-slate-400">Sezione riservata al titolare.</Card>;

  const row = (a) => {
    const shared = a.ssd_tipo !== undefined;
    return <AgendaRow key={a.id} a={a} shared={shared} onOpen={() => (shared ? setTeamEdit(a) : openItem(a))} onToggle={toggle} />;
  };

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2"><NotebookPen className="h-7 w-7 text-primary" /> Agenda Titolare</h1>
          <p className="text-slate-500 mt-1">Appuntamenti, chiamate, colloqui e lavori da fare. Privata: la vedi solo tu. In azzurro gli eventi condivisi della Programmazione.</p>
        </div>
        <Button data-testid="add-agenda-button" onClick={() => { setEditing(null); setPreset(""); setOpen(true); }} className="rounded-full gap-1.5"><Plus className="h-4 w-4" /> Nuova voce</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Section tid="agenda-late" title="In ritardo" icon={AlertTriangle} tone="text-rose-600" list={lists.late} render={row} />
        <Section tid="agenda-today" title="Oggi" icon={CheckCircle2} tone="text-primary" list={lists.today} render={row} />
        <Section tid="agenda-next" title="Prossimi 7 giorni" icon={NotebookPen} tone="text-slate-700 dark:text-slate-200" list={lists.next} render={row} />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="agenda-cal-prev" onClick={() => move(-1)} className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronLeft className="h-5 w-5" /></button>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white" data-testid="agenda-cal-title">{MESI[cursor.m]} {cursor.y}</h2>
          <button data-testid="agenda-cal-next" onClick={() => move(1)} className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{GIORNI.map((g) => <div key={g} className="px-2">{g}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => (
            <div key={i} data-testid={c ? `agenda-day-${c.ds}` : undefined} onClick={() => c && (setEditing(null), setPreset(c.ds), setOpen(true))}
              className={`min-h-[86px] rounded-lg p-1.5 ${c ? "border border-slate-100 dark:border-slate-800 cursor-pointer hover:border-primary/40" : ""} ${c?.ds === today ? "ring-2 ring-primary/40" : ""}`}>
              {c && <div className="text-xs font-semibold text-slate-500 mb-1">{c.d}</div>}
              {c?.mine.map((a) => (
                <div key={a.id} data-testid={`agenda-cal-item-${a.id}`} onClick={(e) => { e.stopPropagation(); openItem(a); }} className={`cursor-pointer hover:ring-1 hover:ring-primary text-[10px] truncate rounded px-1 py-0.5 mb-0.5 ${a.stato === "fatto" ? "bg-slate-100 text-slate-400 line-through dark:bg-slate-800" : "bg-primary/15 text-primary font-semibold"}`}>{a.ora ? `${a.ora} ` : ""}{a.titolo}</div>
              ))}
              {c?.team.map((a) => (
                <div key={a.id} data-testid={`agenda-cal-team-${a.id}`} onClick={(e) => { e.stopPropagation(); setTeamEdit(a); }} className="cursor-pointer hover:ring-1 hover:ring-sky-400 text-[10px] truncate rounded px-1 py-0.5 mb-0.5 bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">{a.titolo}</div>
              ))}
            </div>
          ))}
        </div>
      </Card>

      <AttivitaDialog open={!!teamEdit} onOpenChange={(v) => !v && setTeamEdit(null)} initial={teamEdit} canInvite
        fixedSsd={teamEdit ? { id: teamEdit.ssd_id, tipo: teamEdit.ssd_tipo, nome: teamEdit.ssd_nome } : null}
        onSave={saveTeam} onDelete={deleteTeam} />

      <AgendaDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} initial={editing} portfolio={portfolio} defaultDate={preset}
        onSave={async (d) => { await save(editing, d); setOpen(false); setEditing(null); }}
        onDelete={async (a) => { if (await remove(a)) { setOpen(false); setEditing(null); } }} />
    </div>
  );
}
